// Shared status → antd Tag colour maps for the duty-assistant pages, so conversation states and
// per-car statuses render with consistent, scannable colours across Conversations and Customers.
import React from 'react'
import { Tag } from 'antd'
import { WhatsAppOutlined, MobileOutlined } from '@ant-design/icons'
import { labelFor } from '../../utils/wireKeys'

// Conversation lifecycle state (DutyConversation.state).
export const CONV_STATE_COLORS = {
  idle: 'default',
  collecting: 'blue',
  awaiting_payment: 'gold',
  processing: 'orange',
  delivering: 'cyan',
  closed: 'default'
}

// Per-car duty-request status (DutyCarRequest.status) — the funnel a single car moves through.
export const CAR_STATUS_COLORS = {
  collecting: 'blue',
  ready: 'geekblue',
  awaiting_payment: 'gold',
  paid: 'green',
  processing: 'orange',
  completed: 'green',
  failed: 'red',
  cancelled: 'default'
}

// Human labels for car statuses (underscores → spaces, title-ish).
const CAR_STATUS_LABELS = {
  awaiting_payment: 'awaiting payment'
}

export const carStatusTag = (status, key) => (
  <Tag key={key} color={CAR_STATUS_COLORS[status] || 'default'} style={{ marginBottom: 2 }}>
    {CAR_STATUS_LABELS[status] || status}
  </Tag>
)

// Which surface a conversation came in on (DutyConversation.channel). The two are the SAME rows —
// same table, same slots, same finalizer — so nothing downstream branches on this; it exists so
// support knows what they are looking at before they act on it. A WhatsApp customer is reachable
// by replying in the thread; an app customer may not be on WhatsApp at all.
//
// WhatsApp keeps its own brand green rather than an antd preset, because that colour IS the
// recognition — support scans this column, they do not read it.
const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', color: 'green', icon: <WhatsAppOutlined /> },
  app: { label: 'Mobile app', color: 'geekblue', icon: <MobileOutlined /> }
}

export const CHANNEL_OPTIONS = Object.keys(CHANNEL_META).map(value => ({
  value,
  label: CHANNEL_META[value].label
}))

// A row written before the field existed reads as WhatsApp, which is what it was — the app came
// later. An unknown value is shown verbatim rather than guessed at.
export const channelTag = (channel, key) => {
  const meta = CHANNEL_META[channel || 'whatsapp']
  if (!meta) {
    return (
      <Tag key={key} color="default">
        {channel}
      </Tag>
    )
  }
  return (
    <Tag key={key} color={meta.color} icon={meta.icon} style={{ marginInlineEnd: 0 }}>
      {meta.label}
    </Tag>
  )
}

export const isAppChannel = channel => channel === 'app'

// Which service a conversation is about (DutyConversation.service; spec: assistant-front-door).
// One number now runs several, and support reading this list has to know which one they are
// looking at before they reply — a reply about a duty estimate in a history-report conversation
// is the kind of thing a customer screenshots.
//
// Duty is deliberately the quietest colour. Nearly every conversation is a duty check, and a
// bright tag on the common case trains the eye to ignore the column that matters on the rare one.
const SERVICE_META = {
  duty: { label: 'Duty', color: 'default' },
  vehicle_history: { label: 'History report', color: 'gold' },
  tracking: { label: 'Tracking', color: 'purple' },
  insurance: { label: 'Insurance', color: 'cyan' }
}

export const SERVICE_OPTIONS = Object.keys(SERVICE_META).map(value => ({
  value,
  label: SERVICE_META[value].label
}))

// A row written before the field existed reads as Duty, which is what every one of them was.
// A value this build does not know is shown verbatim rather than guessed at — a service added
// server-side should look unfamiliar, not wrong.
export const serviceTag = (service, key) => {
  const meta = SERVICE_META[service || 'duty']
  if (!meta) {
    return (
      <Tag key={key} color="default">
        {service}
      </Tag>
    )
  }
  return (
    <Tag key={key} color={meta.color} style={{ marginInlineEnd: 0 }}>
      {meta.label}
    </Tag>
  )
}

export const serviceLabel = service =>
  labelFor(SERVICE_META, service || 'duty')?.label || service || '—'

// Where each service's conversations live in the dashboard (owner, 2026-10-29): every module has
// its own Conversations page, showing only its own. A link to a conversation goes to its module's
// page, so the sidebar stays lit on the section the conversation belongs to.
export const CONVERSATION_PATHS = {
  duty: '/duty-assistant/conversations',
  vehicle_history: '/vehicle-history/conversations',
  tracking: '/tracking/conversations'
}

export const conversationPath = (service, id) => {
  const base = CONVERSATION_PATHS[service || 'duty'] || CONVERSATION_PATHS.duty
  return id ? `${base}/${id}` : base
}
